import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Reactive Teeodore floating companion. Lives in the bottom-right corner.
 * Idle floats gently. Reacts to:
 *  - Hovering any Shop card / Buy button → thumbs-up + "solid choice"
 *  - Hovering the Custom form submit → "let's go"
 *  - Clicking Teeodore himself → random one-liner from his rotation
 *  - Scrolling to the Photo Dump section → holds up phone taking pics
 *
 * Also plays real voice audio (ElevenLabs "Roger" voice) when the user has
 * enabled the 🔊 toggle. Voice is OFF by default so we don't ambush people.
 *
 * Auto-hides on very narrow screens so mobile UX isn't blocked.
 */

// Each speech line is tied to a pre-generated MP3 clip. Keep the audio
// filenames matching /public/audio/teeodore/<key>.mp3.
const LINES = {
  clickThrough: [
    { key: 'great-idea', text: 'Great idea.' },
    { key: 'shirt-slaps', text: 'That shirt slaps.' },
    { key: 'throne-agrees', text: 'The throne agrees.' },
    { key: 'send-it', text: 'Send it.' },
    { key: 'studio-approved', text: 'Studio-approved.' },
    { key: 'chefs-kiss', text: 'Chef\u2019s kiss.' },
    { key: 'send-to-press', text: 'Send that to press.' },
    { key: 'you-cooked', text: 'You cooked.' },
    { key: 'cso-approves', text: 'CSO approves.' },
    { key: 'bathroom-boardroom', text: 'Bathroom-boardroom brilliance.' },
  ],
  shopHover: [
    { key: 'good-pick', text: 'Ooh \u2014 good pick.' },
    { key: 'nice-choice', text: 'Nice choice.' },
    { key: 'winner', text: 'That one\u2019s a winner.' },
    { key: 'absolute-unit', text: 'Absolute unit.' },
    { key: 'add-to-cart', text: 'Add to cart. Trust me.' },
  ],
  hotIdea: { key: 'hot-idea', text: 'Now THAT is a shirt idea.' },
  sayCheese: { key: 'say-cheese', text: 'Say cheese.' },
};

// Random helper — pick different lines without repeating twice in a row.
const useRandomPicker = () => {
  const lastRef = useRef(-1);
  return (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    let idx = Math.floor(Math.random() * arr.length);
    if (arr.length > 1 && idx === lastRef.current) {
      idx = (idx + 1) % arr.length;
    }
    lastRef.current = idx;
    return arr[idx];
  };
};

const TeeodoreCompanion = () => {
  const [visible, setVisible] = useState(true);
  const [bubble, setBubble] = useState(null); // { text, key }
  const [dismissed, setDismissed] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const pick = useRandomPicker();
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  // Dismissed state persists per-session so users who close it aren't nagged.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem('teeodore-dismissed') === '1') {
      setDismissed(true);
    }
    if (localStorage.getItem('teeodore-voice') === 'on') {
      setVoiceOn(true);
    }
  }, []);

  const playAudio = (soundKey) => {
    if (!voiceOn || !soundKey) return;
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      const audio = new Audio(`/audio/teeodore/${soundKey}.mp3`);
      audio.volume = 0.85;
      audioRef.current = audio;
      // Ignore autoplay-block errors silently — browser will require a
      // gesture before the very first playback anyway.
      audio.play().catch(() => {});
    } catch { /* no-op */ }
  };

  const speak = (line) => {
    if (!line || dismissed) return;
    const text = typeof line === 'string' ? line : line.text;
    const soundKey = typeof line === 'object' ? line.key : null;
    if (timerRef.current) clearTimeout(timerRef.current);
    setBubble({ text, key: Date.now() });
    playAudio(soundKey);
    timerRef.current = setTimeout(() => setBubble(null), 3800);
  };

  // Delegated global listeners for the reactions.
  useEffect(() => {
    if (dismissed || !visible) return;

    const onOver = (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.closest('[data-testid^="home-shop-buy-"]')) {
        speak(pick(LINES.shopHover));
        return;
      }
      if (target.closest('[data-testid="custom-roll-idea"]')) {
        speak(LINES.hotIdea);
        return;
      }
    };

    let scrollTicking = false;
    const onScroll = () => {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(() => {
        scrollTicking = false;
        const gallery = document.querySelector('[data-testid="home-gallery-section"]');
        if (!gallery) return;
        const rect = gallery.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.4 && rect.bottom > window.innerHeight * 0.4) {
          if (!sessionStorage.getItem('teeodore-seen-gallery')) {
            sessionStorage.setItem('teeodore-seen-gallery', '1');
            speak(LINES.sayCheese);
          }
        }
      });
    };

    document.addEventListener('mouseover', onOver, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mouseover', onOver);
      window.removeEventListener('scroll', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissed, visible, voiceOn]);

  if (!visible || dismissed) return null;

  const onSelfClick = () => speak(pick(LINES.clickThrough));

  const onDismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem('teeodore-dismissed', '1');
    setDismissed(true);
  };

  const toggleVoice = (e) => {
    e.stopPropagation();
    const next = !voiceOn;
    setVoiceOn(next);
    localStorage.setItem('teeodore-voice', next ? 'on' : 'off');
    if (next) {
      // Nice touch: greet the user with a voice sample the first time.
      setTimeout(() => speak({ key: 'on-duty', text: 'Chief Shirt Officer, on duty.' }), 200);
    }
  };

  return (
    <div
      data-testid="teeodore-companion"
      className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2 pointer-events-none select-none"
    >
      <AnimatePresence>
        {bubble && (
          <motion.div
            key={bubble.key}
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="max-w-[180px] sm:max-w-[220px] rounded-2xl bg-white border-2 border-black shadow-[4px_4px_0_#FF6600] px-3 py-2 sm:px-4 sm:py-2.5 pointer-events-auto"
          >
            <div className="font-body text-sm text-black leading-snug"
              dangerouslySetInnerHTML={{ __html: bubble.text }} />
            <div className="absolute -bottom-[9px] right-8 w-4 h-4 bg-white border-b-2 border-r-2 border-black rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative pointer-events-auto">
        {/* Voice toggle — small pill on the left of Teeodore */}
        <button
          type="button"
          onClick={toggleVoice}
          data-testid="teeodore-voice-toggle"
          title={voiceOn ? 'Mute Teeodore' : 'Unmute Teeodore\u2019s voice'}
          className={[
            'absolute -left-3 -top-1 z-10 rounded-full border-2 border-black w-8 h-8 flex items-center justify-center text-sm shadow-[2px_2px_0_#000] transition-colors',
            voiceOn
              ? 'bg-brand-orange text-black hover:bg-black hover:text-brand-orange'
              : 'bg-white text-black hover:bg-brand-orange',
          ].join(' ')}
        >
          {voiceOn ? '🔊' : '🔇'}
        </button>

        <motion.button
          type="button"
          onClick={onSelfClick}
          aria-label="Talk to Teeodore"
          className="relative focus:outline-none group"
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.94 }}
          animate={{ y: [0, -8, 0] }}
          transition={{ y: { duration: 3.2, repeat: Infinity, ease: 'easeInOut' } }}
        >
          <img
            src="/brand/teeodore-sm.png"
            alt="Teeodore, Chief Shirt Officer"
            className="w-16 sm:w-20 md:w-24 h-auto drop-shadow-[0_10px_20px_rgba(255,102,0,0.45)]"
            draggable={false}
          />
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss Teeodore"
            className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-black text-white/70 hover:text-white text-xs font-black opacity-0 group-hover:opacity-100 transition-opacity border-2 border-brand-orange flex items-center justify-center pointer-events-auto"
          >
            ×
          </button>
        </motion.button>
      </div>
    </div>
  );
};

export default TeeodoreCompanion;
